import {
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { FilePondModule } from 'ngx-filepond';
import acceptedImages from '../../../../../../common/constants/acceptedImages';
import PictureTypes from '../../../../../../common/enums/pictureTypes';

import { ImageType } from '../../../../../../core/data/models/ImageType';
import { NgxMaskDirective } from 'ngx-mask';
import DriverRelationType from '../../../../../../common/enums/driverRelationType';
import { SharedModule } from '../../../../../../common/shared.module';
import { GoogleMapsModule } from '@angular/google-maps';
import { LocationPickerComponent } from '../../../../../../common/components/location-picker/location-picker.component';
import policeDepartmentTranslation from '../../../../../../common/constants/policeDepartmentTranslation';
import PoliceDepartment from '../../../../../../common/enums/policeDepartment';
import { FormInputDirective } from '../../../../../../common/directives/form-input.directive';

export interface Injured {
  name: string;
  injury: string;
}

@Component({
  selector: 'app-vehicle-form',
  imports: [
    FilePondModule,
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    SharedModule,
    GoogleMapsModule,
    LocationPickerComponent,
  ],
  templateUrl: './vehicle-form.component.html',
  styleUrl: './vehicle-form.component.css',
})
export class VehicleFormComponent {
  @ViewChildren(FormInputDirective)
  validatedInputs!: QueryList<FormInputDirective>;
  @ViewChildren(LocationPickerComponent)
  locationPickers!: QueryList<LocationPickerComponent>;

  @Input() vehicleForm!: FormGroup;
  @Input({ required: true }) showDriverForm!: boolean;
  @Input() vehicleLocationCenter!: google.maps.LatLngLiteral;
  @Input() zoom: number = 15;
  @Output() vehicleDragEnd = new EventEmitter<any>();
  @Output() vehicleAddressChange = new EventEmitter<string | undefined>();
  @Output() onImagesChange = new EventEmitter<
    { file: File; type: PictureTypes }[]
  >();
  @Output() onInjuredAdded = new EventEmitter<Injured[]>();

  pictureTypes = PictureTypes;
  idType: 'id' | 'passport' = 'id';
  hasPropertyDamage = false;
  hasInjured = false;
  impactZones = [
    'Frontal izquierdo',
    'Frontal derecho',
    'Capó',
    'Puerta del conductor',
    'Parabrisas delantero',
    'Puerta del copiloto',
    'Puerta trasera izquierda',
    'Techo',
    'Puerta trasera derecha',
    'Defensa trasera izquierda',
    'Maletero',
    'Defensa trasera derecha',
  ];
  relations = [
    { id: DriverRelationType.Employee, name: 'Empleado' },
    { id: DriverRelationType.Family, name: 'Familia' },
    { id: DriverRelationType.Friend, name: 'Amistad' },
    { id: DriverRelationType.Client, name: 'Cliente' },
  ];
  injuredList: Injured[] = [];
  injuredName: string = '';
  injuredLesion: string = '';
  newImages: (ImageType & { id: number })[] = [];
  picOptions = {
    multiple: false,
    labelIdle: 'Arrastra la imagen o click para elegir',
    acceptedFileTypes: acceptedImages,
    allowReorder: true,
  };
  picOptionsMultiple = {
    ...this.picOptions,
    labelIdle: 'Arrastra las imágenes o click para elegir',
    multiple: true,
  };
  protected readonly Object = Object;
  protected readonly policeDepartmentTranslation = policeDepartmentTranslation;

  get propertyDamageGroup() {
    return this.vehicleForm.get('propertyDamage') as FormGroup;
  }

  get departmentsValues() {
    return Object.values(PoliceDepartment).filter(
      (value) => typeof value === 'number',
    ) as unknown as number[];
  }

  get isTowed() {
    return this.vehicleForm.get('vehicle')?.get('isTowed')?.value;
  }

  // Adapter to emit a Google Maps-like event for parent compatibility
  emitVehicleEvent($event: {
    location: google.maps.LatLngLiteral;
    fullAddress?: string;
    street?: string;
  }) {
    if (!$event || !$event.location) return;
    const loc = $event.location;
    const fakeEvent = {
      latLng: {
        lat: () => loc.lat,
        lng: () => loc.lng,
      },
      fullAddress: $event.fullAddress,
      street: $event.street,
    } as any;

    this.vehicleDragEnd.emit(fakeEvent);
  }

  // Update driver address from LocationPicker input changes
  handleAddressChanged(address?: string) {
    this.vehicleAddressChange.emit(address);
  }

  handleAddImage($event: any, type: PictureTypes) {
    this.newImages.push({
      id: $event.file.id,
      file: $event.file.file,
      type,
    });
    this.onImagesChange.emit(
      this.newImages.map((image) => ({ file: image.file, type: image.type })),
    );
  }

  handleRemoveImage($event: any) {
    const index = this.newImages.findIndex(
      (image) => image.id === $event.file.id,
    );

    if (index > -1) {
      this.newImages.splice(index, 1);
    }

    this.onImagesChange.emit(
      this.newImages.map((image) => ({ file: image.file, type: image.type })),
    );
  }

  addInjured() {
    this.injuredList.push({
      name: this.injuredName,
      injury: this.injuredLesion,
    });
    this.injuredName = '';
    this.injuredLesion = '';
    this.onInjuredAdded.emit(this.injuredList);
  }

  removeInjured(index: number) {
    this.injuredList.splice(index, 1);
    this.onInjuredAdded.emit(this.injuredList);
  }

  handlePropertyDamageChange() {
    if (this.hasPropertyDamage) {
      this.propertyDamageGroup.controls['ownerName']!.addValidators([
        Validators.required,
      ]);
      this.propertyDamageGroup.controls['damageDescription']!.addValidators([
        Validators.required,
      ]);
    } else {
      this.propertyDamageGroup.controls['ownerName']!.removeValidators([
        Validators.required,
      ]);
      this.propertyDamageGroup.controls['damageDescription']!.removeValidators([
        Validators.required,
      ]);
    }

    this.propertyDamageGroup.updateValueAndValidity();
  }

  handleHasPoliceRegistryChange() {
    if (this.vehicleForm.value['hasPoliceRegistry']) {
      this.vehicleForm.controls['policeRegistryDepartment'].setValue(
        PoliceDepartment.DriverHouse,
      );
      this.vehicleForm.controls['policeRegistryDepartment'].addValidators([
        Validators.required,
      ]);
    } else {
      this.vehicleForm.controls['policeRegistryDepartment'].clearValidators();
      this.vehicleForm.controls['policeRegistryDepartment'].setValue('');
    }
    this.vehicleForm.controls[
      'policeRegistryDepartment'
    ].updateValueAndValidity();
  }
}
